import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Users, Edit2, Trash2, Shield, Eye, UserCheck, Settings, BarChart3 } from 'lucide-react';
import Button from '../common/Button';
import Card from '../common/Card';
import Loading from '../common/Loading';
import ConfirmModal from '../common/ConfirmModal';
import { eventUserAssignmentAPI } from '../../api/endpoints';
import { useToastContext } from '../../context/ToastContext';

const UserAssignmentsList = ({ event, onEditAssignment }) => {
  const queryClient = useQueryClient();
  const toast = useToastContext();
  const [deleteConfirm, setDeleteConfirm] = React.useState({ open: false, assignment: null });

  // Fetch user assignments
  const { data: assignmentsData, isLoading } = useQuery({
    queryKey: ['event-user-assignments', event.id],
    queryFn: () => eventUserAssignmentAPI.getAll(event.id),
    enabled: !!event.id
  });

  // Delete assignment mutation
  const deleteAssignmentMutation = useMutation({
    mutationFn: (assignmentId) => eventUserAssignmentAPI.delete(event.id, assignmentId),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['event-user-assignments', event.id]);
      toast.success(response.data.message || 'User assignment removed successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to remove user assignment');
    }
  });

  const handleDeleteAssignment = (assignment) => {
    setDeleteConfirm({ open: true, assignment });
  };
  
  const confirmDeleteAssignment = () => {
    if (deleteConfirm.assignment) {
      deleteAssignmentMutation.mutate(deleteConfirm.assignment.id);
    }
    setDeleteConfirm({ open: false, assignment: null });
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <Shield className="h-4 w-4 text-red-600" />;
      case 'usher':
        return <UserCheck className="h-4 w-4 text-blue-600" />;
      case 'viewer':
        return <Eye className="h-4 w-4 text-gray-600" />;
      default:
        return <Users className="h-4 w-4 text-gray-400" />;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'usher':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'viewer':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const getPermissionIcon = (permission) => {
    switch (permission) {
      case 'can_manage_attendees':
        return <Users className="h-3 w-3" />;
      case 'can_manage_fields':
        return <Settings className="h-3 w-3" />;
      case 'can_manage_badges':
        return <UserCheck className="h-3 w-3" />;
      case 'can_view_analytics':
        return <BarChart3 className="h-3 w-3" />;
      default:
        return null;
    }
  };

  const getPermissionLabel = (permission) => {
    switch (permission) {
      case 'can_manage_attendees':
        return 'Attendees';
      case 'can_manage_fields':
        return 'Fields';
      case 'can_manage_badges':
        return 'Badges';
      case 'can_view_analytics':
        return 'Analytics';
      default:
        return permission;
    }
  };

  if (isLoading) {
    return <Loading text="Loading user assignments..." />;
  }

  const assignments = assignmentsData?.data || [];

  if (assignments.length === 0) {
    return (
      <Card>
        <Card.Content className="text-center py-8">
          <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No User Assignments</h3>
          <p className="text-gray-600 mb-4">
            No users have been assigned to this event yet.
          </p>
        </Card.Content>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          Assigned Users ({assignments.length})
        </h3>
      </div>

      <div className="grid gap-4">
        {assignments.map((assignment) => (
          <Card key={assignment.id} className="hover:shadow-md transition-shadow">
            <Card.Content className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="flex items-center gap-2">
                      {getRoleIcon(assignment.role)}
                      <div>
                        <h4 className="font-semibold text-gray-900">
                          {assignment.user?.name || 'Unknown User'}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {assignment.user?.email}
                        </p>
                      </div>
                    </div>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold border ${getRoleBadgeColor(assignment.role)}`}>
                      {assignment.role_label || assignment.role}
                    </span>
                  </div>

                  {/* Permissions */}
                  {assignment.permissions && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {Object.entries(assignment.permissions)
                        .filter(([key, value]) => value === true)
                        .map(([permission]) => (
                          <span
                            key={permission}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full"
                          >
                            {getPermissionIcon(permission)}
                            {getPermissionLabel(permission)}
                          </span>
                        ))}
                    </div>
                  )}

                  <div className="text-xs text-gray-500">
                    Assigned by {assignment.assigned_by?.name || 'Unknown'} • {' '}
                    {new Date(assignment.created_at).toLocaleDateString()}
                  </div>
                </div>

                <div className="flex items-center gap-2 ml-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onEditAssignment && onEditAssignment(assignment)}
                    className="gap-1"
                  >
                    <Edit2 className="h-3 w-3" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => handleDeleteAssignment(assignment)}
                    loading={deleteAssignmentMutation.isPending}
                    className="gap-1"
                  >
                    <Trash2 className="h-3 w-3" />
                    Remove
                  </Button>
                </div>
              </div>
            </Card.Content>
          </Card>
        ))}
      </div>
      
      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, assignment: null })}
        onConfirm={confirmDeleteAssignment}
        title="Remove User Assignment"
        message={`Are you sure you want to remove ${deleteConfirm.assignment?.user?.name} from this event?`}
        confirmText="Remove"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

export default UserAssignmentsList;
